import { maxBrandingImageBytes, type BrandingAssetKind, type PlatformBranding } from '@smarton-pbx/shared';
import { ImageIcon, Trash2, Upload } from 'lucide-react';
import { useState } from 'react';
import { api } from '../api';
import { Loading, Notice, PageHeader } from '../components/ui';
import { t } from '../i18n';
import { useLoad } from '../lib/hooks';

const kinds: { kind: BrandingAssetKind; label: string; alt: string; hint: string; version: keyof PlatformBranding }[] = [
  { kind: 'logo', label: t('Logo'), alt: t('Default logo'), hint: t('Wide, on a transparent background. PNG, SVG or WebP, up to 512 KB.'), version: 'logoVersion' },
  { kind: 'favicon', label: t('Icon'), alt: t('Default icon'), hint: t('Square, for browser tabs and app icons. PNG or ICO.'), version: 'faviconVersion' },
];

/**
 * The default logo and icon: shown on every tenant's site, installable app,
 * notifications and phone app until that tenant uploads its own.
 */
export function DefaultBranding() {
  const loaded = useLoad(api.platform.defaultBranding);
  const [branding, setBranding] = useState<PlatformBranding | null>(null);
  const [error, setError] = useState<string>();
  const current = branding ?? loaded.data ?? null;

  async function act(work: () => Promise<PlatformBranding>) {
    setError(undefined);
    try {
      setBranding(await work());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  function upload(kind: BrandingAssetKind, file: File | undefined) {
    if (!file) return;
    if (file.size > maxBrandingImageBytes) return setError(t('Images can be up to 512 KB.'));
    void act(() => api.platform.uploadDefaultImage(kind, file));
  }

  return (
    <div className="stack">
      <PageHeader
        title={t('Default branding')}
        description={t('Shown for every company that hasn’t uploaded its own logo or icon: on its site, its installable app, notifications and the phone app. A company’s own upload replaces it.')}
      />
      <Notice>{t('Companies you sell to under their own brand see this until they upload theirs.')}</Notice>
      {loaded.error && <Notice kind="error">{loaded.error}</Notice>}
      {!current ? (
        <Loading />
      ) : (
        <div className="card">
          <div className="card-body image-tiles">
            {kinds.map(({ kind, label, alt, hint, version }) => {
              const v = current[version];
              return (
                <div key={kind} className="image-tile" data-testid={`default-${kind}`}>
                  <div>
                    <h3>{label}</h3>
                    <div className="subtle">{hint}</div>
                  </div>
                  <div className="image-box">
                    {v ? <img src={api.platform.defaultImageUrl(kind, v)} alt={alt} /> : <ImageIcon size={22} className="subtle" aria-hidden />}
                  </div>
                  <div className="row">
                    <label className="button secondary small">
                      <Upload size={15} aria-hidden /> {v ? t('Replace') : t('Upload')}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon"
                        hidden
                        onChange={(e) => {
                          upload(kind, e.target.files?.[0]);
                          e.target.value = '';
                        }}
                      />
                    </label>
                    {v && (
                      <button type="button" className="ghost small" onClick={() => void act(() => api.platform.removeDefaultImage(kind))}>
                        <Trash2 size={15} aria-hidden /> {t('Remove')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {error && <Notice kind="error">{error}</Notice>}
    </div>
  );
}
